import {
    FulfillmentContextProviderFromPosition,
    useFulfillmentContext,
} from '@/modules/fulfillment/context/FulfillmentContext.tsx';
import { Button } from '@schuettflix/react-components';
import React, { useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { BeforeUnloadContext } from '@/shared/context/BeforeUnloadContext.tsx';
import { FulfillmentUploadProvider } from './context/FulfillmentUploadContext';
import { usePosition } from '@/clients/position/usePosition';
import { DirectFulfillmentConfirmation } from './components/DirectFulfillmentConfirmation';
import { usePostPositionFulfillmentMutation } from '@/clients/manualFulfillment/usePostPositionFulfillmentMutation';
import { FulfillmentBaseFrame } from './components/FulfillmentBaseFrame';
import { FulfillmentBaseContent } from './components/FulfillmentBaseContent';
import { NonEmptyArray } from '@/shared/types/NonEmptyArray';
import {
    FinanceSettingsLocalStateProvider,
    useFinanceSettingsLocalState,
} from '@/shared/components/FinanceSettings/context/FinanceSettingsLocalStateContext';
import { GetPositionConfigurationResponse } from '@/shared/types/positionV2/api/get-position-configuration.ts';
import { useSetDocumentsAsBilled } from '@/modules/fulfillment/hooks/useSetDocumentsAsBilled.ts';
import { useConstructionProject } from '@/clients/constructionProject/useConstructionProject.ts';

export const DirectFulfillmentPage: React.FC<{ position: GetPositionConfigurationResponse }> = ({ position }) => {
    const location = useLocation();

    return (
        <FulfillmentBaseFrame>
            {/* Making sure that we reload the FulfillmentContext whenever a navigation happens */}
            <FulfillmentContextProviderFromPosition positionId={position.id} key={location.key}>
                <FulfillmentUploadProvider>
                    <FinanceSettingsLocalStateProvider>
                        <DirectFulfillmentContent positionId={position.id} />
                    </FinanceSettingsLocalStateProvider>
                </FulfillmentUploadProvider>
            </FulfillmentContextProviderFromPosition>
        </FulfillmentBaseFrame>
    );
};

const DirectFulfillmentContent = ({ positionId }: { positionId: string }) => {
    const {
        products,
        documents,
        errors,
        setSubmitted,
        isPageValid,
        canSubmit,
        getLineItemListingWithProductFallback,
        lineItemListing,
        contextSource,
        order,
        getFulfillmentQuoteByOrderProductId,
    } = useFulfillmentContext();
    if (contextSource !== 'position')
        throw new Error(
            'The DirectFulfillmentPage expects to have a fulfillmentContext with the contextSource === position'
        );

    const { data: position } = usePosition(positionId);
    const { data: constructionProject } = useConstructionProject(position.customerInfo.constructionProjectId);

    const { t } = useTranslation();

    const beforeUnloadContext = useContext(BeforeUnloadContext);
    const setDocumentsAsBilled = useSetDocumentsAsBilled();

    const {
        mutate: fulfillmentMutation,
        isPending: fulfillmentMutationIsPending,
        data: fulfillmentMutationData,
    } = usePostPositionFulfillmentMutation({
        onSuccess: async () => {
            if (lineItemListing) {
                await setDocumentsAsBilled(lineItemListing, documents);
            }

            setShowDirectFulfillmentConfirmation(true);
        },
    });

    const [showDirectFulfillmentConfirmation, setShowDirectFulfillmentConfirmation] = useState(false);

    const { getPaymentOrganizationsByProductId } = useFinanceSettingsLocalState();

    const fulfillOrder = useCallback(() => {
        beforeUnloadContext.setEnabled(false);
        setSubmitted(true);

        if (!isPageValid) {
            return errors[0]?.scrollToElement();
        }

        fulfillmentMutation({
            positionId: position.id,
            payload: {
                clientInformation: {
                    userId: order.customerInfo!.userId,
                    manualCostCenter:
                        order.customerInfo?.manualCostCenter ??
                        position.customerInfo.manualCostCenter ??
                        constructionProject?.costCenter?.trim() ??
                        undefined,
                    projectCode: order.customerInfo!.projectCode,
                },
                products: products.map(product => {
                    if (!product.quote) throw new Error(`Product quote for product ${product.id} is missing`);
                    return {
                        name: product.quote.name,
                        amount: product.quote.amount,
                        unit: product.quote.unit,
                        currencyCode: product.quote.currencyCode,
                        index: product.index,
                        partnerOrganizationId: product.quote.partnerOrganizationId,
                        orderingOrganizationId: product.quote.orderingOrganizationId,
                        platformOrganizationId: product.quote.platformOrganizationId,
                        purchasePrice: product.quote.purchasePrice.toNumber(),
                        purchaseTaxClassId: product.quote.purchaseTaxClassId,
                        showZeroPurchasePrice: product.fulfillmentProductConfiguration.showZeroPurchasePrice,
                        showZeroSalesPrice: product.fulfillmentProductConfiguration.showZeroSalesPrice,
                        salesPrice: product.quote.salesPrice.toNumber(),
                        salesTaxClassId: product.quote.salesTaxClassId,
                        serviceDate: new Date(product.quote.serviceDate),
                        productType: product.type,
                        quotePayload: getFulfillmentQuoteByOrderProductId(product.id),
                        paymentOrganizations: getPaymentOrganizationsByProductId(product.id),
                        ...(product.inOriginalOrder
                            ? {
                                  isNewProduct: false,
                                  positionProductId: product.id,
                              }
                            : {
                                  isNewProduct: true,
                                  productId: product.id,
                                  type: product.type,
                                  category: product.category,
                              }),
                    };
                }),
                supportingDocuments: documents.map(document => ({
                    description: 'Invoice Document',
                    urlPath: document.url,
                    fileName: document.fileName,
                })) as NonEmptyArray<{ description: string; urlPath: string; fileName: string }>,
                lineItemListing: getLineItemListingWithProductFallback().map(lineItem => {
                    const isLineItemOfNewProduct = !products.find(product => product.id === lineItem.productId)
                        ?.inOriginalOrder;
                    const { productId, licensePlate, accompanyingDocumentNumber, referenceDocument, ...rest } =
                        lineItem;
                    return {
                        ...rest,
                        licensePlate: !licensePlate || !licensePlate.trim() ? undefined : licensePlate.trim(),
                        accompanyingDocumentNumber:
                            !accompanyingDocumentNumber || !accompanyingDocumentNumber.trim()
                                ? undefined
                                : accompanyingDocumentNumber.trim(),
                        referenceDocument:
                            !referenceDocument || !referenceDocument.trim() ? undefined : referenceDocument.trim(),
                        ...(isLineItemOfNewProduct ? { productId } : { positionProductId: productId }),
                    };
                }),
                fulfilledWithIndividualItemListing: !!lineItemListing,
            },
        });
    }, [
        beforeUnloadContext,
        setSubmitted,
        isPageValid,
        fulfillmentMutation,
        position.id,
        position.customerInfo.manualCostCenter,
        constructionProject?.costCenter,
        order.customerInfo,
        products,
        documents,
        getLineItemListingWithProductFallback,
        lineItemListing,
        errors,
        getPaymentOrganizationsByProductId,
        getFulfillmentQuoteByOrderProductId,
    ]);

    if (showDirectFulfillmentConfirmation && order.positionId && position?.customerInfo && fulfillmentMutationData) {
        return (
            <DirectFulfillmentConfirmation
                orderNumber={order.humanReadableId}
                orderId={fulfillmentMutationData.orderId}
                positionId={order.positionId}
                constructionProjectId={position.customerInfo.constructionProjectId}
            />
        );
    }

    return (
        <FulfillmentBaseContent
            headerTitle={t('fulfillment.header.directFulfillment.title', { positionNumber: position.positionNumber })}
            footer={
                <div className="flex w-full max-w-[720px] items-center justify-end">
                    <Button
                        label={t('fulfillment.footer.directFulfillOrderButton.label')}
                        variant="primary"
                        size="sm"
                        disabled={fulfillmentMutationIsPending || !canSubmit}
                        onClick={fulfillOrder}
                        data-test="fulfill-order-button"
                    />
                </div>
            }
        />
    );
};
